import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../lib/api';

// `monthly_target` is stripped by the server for the staff role. The
// /teams route is finance-gated (see routes.tsx) so staff never reach
// this page, but tolerate undefined here anyway so a future UI change
// can't reintroduce the old white-screen.
interface Team {
  id: number;
  name: string;
  monthly_target?: number;
  case_count: number;
  achieved?: number;
}

interface TeamForm {
  name: string;
  monthly_target: string;
}

const emptyForm: TeamForm = { name: '', monthly_target: '' };

export default function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [form, setForm] = useState<TeamForm>(emptyForm);
  const [deletingTeamId, setDeletingTeamId] = useState<number | null>(null);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await apiFetch('/api/teams');
      if (res.ok) setTeams(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const formatCurrency = (v: number | null | undefined) => {
    const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
    return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  const getPerformance = (team: Team) => {
    const target = team.monthly_target ?? 0;
    const achieved = team.achieved ?? 0;
    return target <= 0 ? 0 : Math.round((achieved / target) * 100);
  };
  const getBarColor = (pct: number) => pct >= 100 ? 'var(--success)' : pct >= 50 ? 'var(--warning)' : 'var(--danger)';

  const openAdd = () => { setEditingTeam(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (team: Team) => { setEditingTeam(team); setForm({ name: team.name, monthly_target: String(team.monthly_target ?? '') }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditingTeam(null); setForm(emptyForm); };

  const handleSave = async () => {
    const payload = { name: form.name, monthly_target: parseFloat(form.monthly_target) || 0 };
    if (editingTeam) {
      await apiFetch(`/api/teams/${editingTeam.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    } else {
      await apiFetch('/api/teams', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    }
    closeModal();
    fetchTeams();
  };

  const handleDelete = async (id: number) => {
    await apiFetch(`/api/teams/${id}`, { method: 'DELETE' });
    setDeletingTeamId(null);
    fetchTeams();
  };

  const deletingTeam = teams.find(t => t.id === deletingTeamId);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Teams</h1>
          <p className="page-subtitle">Manage lawyer teams and targets</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Team</button>
      </div>

      {loading ? <p style={{ color: 'var(--text-secondary)' }}>Loading teams...</p> : teams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ color: 'var(--text-secondary)' }}>No teams found. Add your first team to get started.</p>
        </div>
      ) : (
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {teams.map((team) => {
            const pct = getPerformance(team);
            return (
              <div className="card" key={team.id}>
                <div className="card-header">
                  <span className="card-title" style={{ fontSize: '18px' }}>{team.name}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(team)}>Edit</button>
                    <button className="btn btn-sm" style={{ color: 'var(--danger)', border: '1px solid var(--danger)', background: 'transparent' }} onClick={() => setDeletingTeamId(team.id)}>Delete</button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Monthly Target</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(team.monthly_target)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Achieved</span>
                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>{formatCurrency(team.achieved)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Cases</span>
                    <span style={{ fontWeight: 600 }}>{team.case_count}</span>
                  </div>
                </div>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Performance</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: getBarColor(pct) }}>{pct}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: getBarColor(pct) }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeam ? 'Edit Team' : 'Add Team'}</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Team Name</label>
                <input className="form-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Family Law Team" />
              </div>
              <div className="form-group">
                <label className="form-label">Monthly Target (£)</label>
                <input className="form-input" type="number" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} placeholder="e.g. 50000" min="0" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.name.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}

      {deletingTeamId !== null && (
        <div className="modal-overlay" onClick={() => setDeletingTeamId(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Team?</h2>
              <button className="btn-icon" onClick={() => setDeletingTeamId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
                Are you sure you want to delete <strong>{deletingTeam?.name}</strong>?
                {deletingTeam && deletingTeam.case_count > 0 && (
                  <span style={{ display: 'block', marginTop: 8, color: 'var(--warning)' }}>
                    This team has {deletingTeam.case_count} case(s) assigned to it. Those cases will become unassigned.
                  </span>
                )}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeletingTeamId(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--danger)', color: '#fff' }} onClick={() => handleDelete(deletingTeamId)}>Delete Team</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
